import { useEffect, useState } from "preact/hooks";
import { Toasts } from "./components/ui";
import { I18nProvider, useT } from "./i18n";
import { get, post } from "./lib/api";
import { BuildEditor, BuildList } from "./pages/Build";
import { HostLogin, HostVerify } from "./pages/HostAuth";
import { HostConsole } from "./pages/HostConsole";
import { type FacilitatorInfo, HostDashboard, HostNew } from "./pages/HostDashboard";
import { JoinEntry, JoinSession, WatchSession } from "./pages/Join";
import { Landing } from "./pages/Landing";
import { Play } from "./pages/Player";
import { Screen } from "./pages/Screen";
import { Tour } from "./pages/Tour";
import { matchPath, useLocation } from "./router";

function useFacilitator() {
  const [facilitator, setFacilitator] = useState<FacilitatorInfo | null | undefined>(undefined);
  const refresh = () =>
    get<{ facilitator: FacilitatorInfo }>("/api/auth/me")
      .then((r) => setFacilitator(r.facilitator))
      .catch(() => setFacilitator(null));
  useEffect(() => {
    refresh();
  }, []);
  const signOut = async () => {
    await post("/api/auth/logout").catch(() => undefined);
    setFacilitator(null);
  };
  return { facilitator, refresh, signOut };
}

function Routes() {
  const loc = useLocation();
  const { facilitator, refresh, signOut } = useFacilitator();
  const p = loc.path.replace(/\/+$/, "") || "/";

  const join = matchPath("/j/:code", p);
  const play = matchPath("/play/:code", p);
  const watch = matchPath("/watch/:code", p);
  const screen = matchPath("/screen/:code", p);
  const console = matchPath("/host/sessions/:id", p);
  const editor = matchPath("/build/:id", p);

  if (p === "/") return <Landing />;
  if (p === "/tour") return <Tour />;
  if (p === "/join") return <JoinEntry />;
  if (join) return <JoinSession code={join.code!} query={loc.query} />;
  if (play) return <Play code={play.code!.toUpperCase()} />;
  if (watch) return <WatchSession code={watch.code!} />;
  if (screen) return <Screen code={screen.code!.toUpperCase()} query={loc.query} />;

  if (p === "/host/verify") return <HostVerify token={loc.query.get("token")} onSignedIn={refresh} />;
  if (p.startsWith("/host") || p.startsWith("/build")) {
    if (facilitator === undefined) return null;
    if (!facilitator) return <HostLogin onSignedIn={refresh} />;
    if (p === "/host") return <HostDashboard facilitator={facilitator} onSignOut={signOut} />;
    if (p === "/host/new") return <HostNew facilitator={facilitator} onSignOut={signOut} query={loc.query} />;
    if (console) return <HostConsole id={console.id!} facilitator={facilitator} onSignOut={signOut} />;
    if (p === "/build") return <BuildList facilitator={facilitator} onSignOut={signOut} />;
    if (editor) return <BuildEditor id={editor.id!} facilitator={facilitator} onSignOut={signOut} />;
  }
  return <NotFound />;
}

function NotFound() {
  const t = useT();
  return (
    <main id="main" class="container" style={{ paddingBlock: "var(--s-16)" }}>
      <h1>{t("error.notFound")}</h1>
      <a href="/">{t("nav.home")}</a>
    </main>
  );
}

export function App() {
  return (
    <I18nProvider>
      <Routes />
      <Toasts />
    </I18nProvider>
  );
}
